import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, EyeOff, AlertTriangle, Bot, Gauge } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const REGIONS = ["Bern", "Basel", "Lausanne"];
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const Toggle = ({ active, onClick, disabled, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    aria-pressed={active}
    className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${active ? "bg-gold" : "bg-white/15"}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-anthracite transition-transform ${active ? "translate-x-6" : "translate-x-1"}`} />
  </button>
);

const Card = ({ icon: Icon, title, desc, active, activeLabel, inactiveLabel, onToggle, saving, testid }) => (
  <div data-testid={`notfall-card-${testid}`} className={`rounded-2xl border p-6 transition-colors ${active ? "border-gold/50 bg-gold/[0.07]" : "border-white/12 bg-white/[0.03]"}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0 ${active ? "border-gold/50" : "border-white/15"}`} style={{ background: active ? "rgba(198,156,34,0.14)" : "rgba(255,255,255,0.03)" }}>
          <Icon size={20} style={{ color: active ? "#E5C76B" : "rgba(250,250,248,0.55)" }} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-cream">{title}</h3>
          <p className="mt-1 text-sm text-cream/60">{desc}</p>
        </div>
      </div>
      {saving ? <Loader2 size={20} className="animate-spin text-cream/50 mt-1" /> : <Toggle active={active} onClick={onToggle} testid={`notfall-toggle-${testid}`} />}
    </div>
    <div className="mt-4 pt-3 border-t border-white/10">
      <span data-testid={`notfall-status-${testid}`} className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] ${active ? "" : "text-cream/45"}`} style={active ? { color: "#E5C76B" } : {}}>
        <span className={`w-2 h-2 rounded-full ${active ? "bg-gold animate-pulse" : "bg-cream/30"}`} />
        {active ? activeLabel : inactiveLabel}
      </span>
    </div>
  </div>
);

const NotfallPanel = ({ onLogout }) => {
  const [settings, setSettings] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [limitInput, setLimitInput] = useState("");
  const [balanceInput, setBalanceInput] = useState("");
  const limitInit = useRef(false);
  const balanceInit = useRef(false);

  const load = useCallback(async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/admin/emergency`, { headers: authHeader() });
      setSettings(data);
      if (!limitInit.current) {
        setLimitInput(data.bot_token_limit ? String(data.bot_token_limit) : "");
        limitInit.current = true;
      }
      if (!balanceInit.current) {
        setBalanceInput(data.bot_tokens_remaining ? String(data.bot_tokens_remaining) : "");
        balanceInit.current = true;
      }
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      if (!silent) {
        toast.error("Notfall-Einstellungen konnten nicht geladen werden.");
        setSettings({ logins_disabled: false, ghost_mode: false, bots_enabled: true, bot_token_limit_enabled: false, bot_token_limit: 0, bot_tokens_used: 0 });
      }
    }
  }, [onLogout]);

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 5000); // Live-Update Token-Zähler
    return () => clearInterval(iv);
  }, [load]);

  const update = async (key, value, successMsg) => {
    setSavingKey(key);
    try {
      const { data } = await axios.put(`${API}/admin/emergency`, { [key]: value }, { headers: authHeader() });
      setSettings(data);
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingKey(null);
    }
  };

  const saveLimit = () => {
    const n = parseInt(limitInput, 10);
    if (isNaN(n) || n < 0) { toast.error("Bitte eine gültige Zahl eingeben."); return; }
    update("bot_token_limit", n, "Token-Limit gespeichert.");
  };

  const saveBalance = () => {
    const n = parseInt(balanceInput, 10);
    if (isNaN(n) || n < 0) { toast.error("Bitte eine gültige Zahl eingeben."); return; }
    update("bot_tokens_remaining", n, "Token-Guthaben aktualisiert.");
  };

  const toggleRegion = (region) => {
    const current = Array.isArray(settings.bot_regions) ? settings.bot_regions : REGIONS;
    const next = current.includes(region) ? current.filter((r) => r !== region) : [...current, region];
    const ordered = REGIONS.filter((r) => next.includes(r));
    update("bot_regions", ordered, `Kreis ${region} ${next.includes(region) ? "aktiviert" : "deaktiviert"}.`);
  };

  if (settings === null) return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div data-testid="notfall-panel">
      <div className="mb-8">
        <span className="section-eyebrow">Notfall</span>
        <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Notfall-Schalter</h1>
        <p className="mt-2 text-sm text-cream/60">Schnelle Schutzmassnahmen. Der Admin-Login bleibt immer aktiv – du kannst alles jederzeit zurücksetzen.</p>
      </div>

      {(settings.logins_disabled || settings.ghost_mode) && (
        <div data-testid="notfall-active-warning" className="mb-6 flex items-center gap-3 rounded-2xl border border-gold/40 bg-gold/[0.08] px-5 py-3.5">
          <AlertTriangle size={18} style={{ color: "#E5C76B" }} />
          <p className="text-sm text-cream/85">Notfallmassnahmen sind aktiv. Die Seite ist für Besucher eingeschränkt.</p>
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-2">
        <Card
          icon={Lock}
          testid="logins"
          title="Logins deaktivieren"
          desc="Verhindert vorübergehend, dass sich Mitglieder ins Portal einloggen. Dein Admin-Login bleibt davon unberührt."
          active={settings.logins_disabled}
          activeLabel="Logins gesperrt"
          inactiveLabel="Logins aktiv"
          saving={savingKey === "logins_disabled"}
          onToggle={() => update("logins_disabled", !settings.logins_disabled, !settings.logins_disabled ? "Mitglieder-Logins gesperrt." : "Mitglieder-Logins wieder aktiv.")}
        />
        <Card
          icon={EyeOff}
          testid="ghost"
          title="Ghost Mode"
          desc="Die öffentliche Seite zeigt nur noch einen Wartungshinweis (Seite momentan nicht erreichbar). Admin & Portal bleiben erreichbar."
          active={settings.ghost_mode}
          activeLabel="Ghost Mode aktiv"
          inactiveLabel="Seite öffentlich sichtbar"
          saving={savingKey === "ghost_mode"}
          onToggle={() => update("ghost_mode", !settings.ghost_mode, !settings.ghost_mode ? "Ghost Mode aktiviert." : "Ghost Mode deaktiviert.")}
        />
        <Card
          icon={Bot}
          testid="bots"
          title="KI-Chat-Bots"
          desc="Belebt die Kreis-Chats (Bern/Basel/Lausanne): Bots plaudern dezent untereinander und beantworten Fragen. Verbraucht LLM-Guthaben pro Antwort."
          active={settings.bots_enabled}
          activeLabel="Bots aktiv"
          inactiveLabel="Bots deaktiviert"
          saving={savingKey === "bots_enabled"}
          onToggle={() => update("bots_enabled", !settings.bots_enabled, !settings.bots_enabled ? "KI-Bots aktiviert." : "KI-Bots deaktiviert.")}
        />
      </div>

      {/* Region-Auswahl: in welchen Kreis-Chats die Bots schreiben */}
      <div data-testid="notfall-bot-regions" className={`mt-5 rounded-2xl border p-6 transition-colors ${settings.bots_enabled ? "border-gold/25 bg-gold/[0.03]" : "border-white/10 bg-white/[0.02] opacity-60"}`}>
        <div className="flex items-center gap-2 mb-2">
          <Bot size={18} style={{ color: "#E5C76B" }} />
          <h3 className="font-semibold text-cream">Aktive Kreis-Chats</h3>
        </div>
        <p className="text-sm text-cream/60 mb-4">Wähle, in welchen Chats die Bots schreiben. Häkchen entfernt = dort keine Bots.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          {REGIONS.map((region) => {
            const list = Array.isArray(settings.bot_regions) ? settings.bot_regions : REGIONS;
            const on = list.includes(region);
            return (
              <button
                key={region}
                type="button"
                onClick={() => toggleRegion(region)}
                disabled={!settings.bots_enabled || savingKey === "bot_regions"}
                data-testid={`notfall-bot-region-${region}`}
                aria-pressed={on}
                className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3.5 text-left transition-colors disabled:cursor-not-allowed ${on ? "border-gold/50 bg-gold/[0.08]" : "border-white/12 bg-white/[0.03]"}`}
              >
                <span className={`font-semibold ${on ? "text-cream" : "text-cream/55"}`}>{region}</span>
                <span className={`flex h-6 w-6 items-center justify-center rounded-md border flex-shrink-0 ${on ? "border-gold bg-gold" : "border-white/25 bg-transparent"}`}>
                  {on && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1A1A1C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                </span>
              </button>
            );
          })}
        </div>
        {settings.bots_enabled && Array.isArray(settings.bot_regions) && settings.bot_regions.length === 0 && (
          <p className="mt-3 text-[11px] text-red-300/80">Kein Kreis ausgewählt – die Bots schreiben aktuell nirgends.</p>
        )}
      </div>

      <div data-testid="notfall-bot-tokens" className="mt-5 rounded-2xl border border-gold/25 bg-gold/[0.03] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gauge size={18} style={{ color: "#E5C76B" }} />
          <h3 className="font-semibold text-cream">KI-Bot Token-Verbrauch</h3>
        </div>

        <div className="mb-5 rounded-xl border border-gold/30 bg-gold/[0.06] p-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold text-cream/50">Verbleibendes Token-Guthaben</p>
              <p className="mt-1 text-4xl font-semibold" style={{ color: settings.bot_tokens_remaining > 0 ? "#E5C76B" : "#f87171" }} data-testid="bot-tokens-remaining">
                {settings.bot_tokens_remaining.toLocaleString("de-CH")} <span className="text-lg text-cream/45 font-normal">Tokens übrig</span>
              </p>
              {settings.bot_tokens_remaining === 0 && (
                <p className="mt-1 text-[11px] text-red-300/80">Kein Guthaben hinterlegt bzw. aufgebraucht – trage deine verfügbaren Tokens ein, dann zählt die App herunter.</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                value={balanceInput}
                onChange={(e) => setBalanceInput(e.target.value)}
                placeholder="z.B. 500"
                data-testid="bot-token-balance-input"
                className="h-10 w-36 rounded-lg bg-white/5 border border-gold/25 px-3 text-cream placeholder:text-cream/35 focus:outline-none focus:border-gold/60"
              />
              <button
                onClick={saveBalance}
                disabled={savingKey === "bot_tokens_remaining"}
                data-testid="bot-token-balance-save"
                className="h-10 px-4 rounded-lg bg-gold text-anthracite font-semibold text-sm hover:bg-gold-soft transition-colors disabled:opacity-60 flex-shrink-0"
              >
                {savingKey === "bot_tokens_remaining" ? <Loader2 size={16} className="animate-spin" /> : "Guthaben setzen"}
              </button>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-cream/40 border-t border-gold/15 pt-2">Hinweis: Die App liest dein echtes Emergent-Konto nicht aus. Trage hier deine verfügbaren Tokens ein – bei 0 stoppen die Bots automatisch. (ca.-Werte)</p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-xs uppercase tracking-[0.16em] font-semibold text-cream/50">Verbrauchte Tokens (seit Bots an)</p>
            <p className="mt-2 text-3xl font-semibold" style={{ color: "#E5C76B" }} data-testid="bot-tokens-used">
              {settings.bot_tokens_used.toLocaleString("de-CH")}
              {settings.bot_token_limit_enabled && settings.bot_token_limit > 0 && (
                <span className="text-base text-cream/45 font-normal"> / {settings.bot_token_limit.toLocaleString("de-CH")}</span>
              )}
            </p>
            <p className="mt-1 text-[11px] text-cream/40">Zähler wird beim Aus-/Einschalten der Bots zurückgesetzt. (ca.-Wert)</p>
            {settings.bot_token_limit_enabled && settings.bot_token_limit > 0 && (
              <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-gold transition-all" style={{ width: `${Math.min(100, (settings.bot_tokens_used / settings.bot_token_limit) * 100)}%` }} />
              </div>
            )}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-cream">Token-Limit aktiv</p>
                <p className="mt-1 text-xs text-cream/55">Bei Erreichen des Limits schalten sich Bots &amp; Limiter automatisch aus.</p>
              </div>
              {savingKey === "bot_token_limit_enabled"
                ? <Loader2 size={18} className="animate-spin text-cream/50" />
                : <Toggle active={settings.bot_token_limit_enabled} testid="notfall-toggle-limiter" onClick={() => update("bot_token_limit_enabled", !settings.bot_token_limit_enabled, !settings.bot_token_limit_enabled ? "Token-Limit aktiviert." : "Token-Limit deaktiviert.")} />}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <input
                type="number"
                min="0"
                value={limitInput}
                onChange={(e) => setLimitInput(e.target.value)}
                placeholder="z.B. 100000"
                data-testid="bot-token-limit-input"
                className="h-10 w-full rounded-lg bg-white/5 border border-white/15 px-3 text-cream placeholder:text-cream/35 focus:outline-none focus:border-gold/60"
              />
              <button
                onClick={saveLimit}
                disabled={savingKey === "bot_token_limit"}
                data-testid="bot-token-limit-save"
                className="h-10 px-4 rounded-lg bg-gold text-anthracite font-semibold text-sm hover:bg-gold-soft transition-colors disabled:opacity-60 flex-shrink-0"
              >
                {savingKey === "bot_token_limit" ? <Loader2 size={16} className="animate-spin" /> : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotfallPanel;
